import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Upload as ImportIcon } from 'lucide-react';
import CSVImportModal from '../../components/CSVImportModal';
import { uploadGposCSV, downloadGpoTemplate } from '../../config/api';
import { 
  Plus, 
  Search, 
  Filter, 
  Building2, 
  MapPin, 
  FileText, 
  Users, 
  TrendingUp,
  Edit,
  Trash2,
  Eye,
  ChevronDown,
  ChevronUp,
  ArrowUpDown
} from 'lucide-react';
import { getGpos, deleteGpo, getGpoStatistics } from '../../config/api';
import { toast } from 'react-hot-toast';
import GPOModal from '../../components/GPOModal';

const GPODashboard = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  // State for filters and pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showFilters, setShowFilters] = useState(false);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editingGpo, setEditingGpo] = useState(null);
  const [viewingGpo, setViewingGpo] = useState(null);

  // Fetch GPOs
  const { data: gposData, isLoading, error } = useQuery({
    queryKey: ['gpos', page, limit, search, stateFilter, countryFilter, sortBy, sortOrder],
    queryFn: () => getGpos({
      page,
      limit,
      search: search || undefined,
      state: stateFilter || undefined,
      country: countryFilter || undefined,
      sortBy,
      sortOrder
    }),
    retry: 1
  });

  // Fetch GPO statistics
  const { data: statsData } = useQuery({
    queryKey: ['gpoStatistics'],
    queryFn: getGpoStatistics,
    
      retry: 1
    
  });

  // Delete GPO mutation
  const deleteGpoMutation = useMutation({
    mutationFn: deleteGpo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gpos'] });
      queryClient.invalidateQueries({ queryKey: ['gpoStatistics'] });
      toast.success('GPO deleted successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to delete GPO');
    }
  });

  const handleSearch = (value) => {
    setSearch(value);
    setPage(1);
  };

  const handleFilterChange = (key, value) => {
    if (key === 'state') setStateFilter(value);
    if (key === 'country') setCountryFilter(value);
    setPage(1);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    return sortOrder === 'asc' ? 
      <ChevronUp className="h-4 w-4 text-blue-600" /> : 
      <ChevronDown className="h-4 w-4 text-blue-600" />;
  };

  const handleEdit = (gpo) => {
    setEditingGpo(gpo);
    setShowModal(true);
  };

  const handleView = (gpo) => {
    setViewingGpo(gpo);
    setShowModal(true);
  };

  const handleDelete = async (gpo) => {
    if (window.confirm(`Are you sure you want to delete "${gpo.name}"? This action cannot be undone.`)) {
      await deleteGpoMutation.mutateAsync(gpo.id);
    }
  };

  const handleCreateNew = () => {
    setEditingGpo(null);
    setViewingGpo(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingGpo(null);
    setViewingGpo(null);
  };

  const gpos = gposData?.data?.gpos || [];
  const pagination = gposData?.data?.pagination || {};
  const stats = statsData?.data || {};

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-xl font-semibold mb-2">Error Loading GPOs</div>
          <div className="text-gray-600 mb-4">Failed to load GPOs. Please try again.</div>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            title="Retry"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Group Purchasing Organizations</h1>
              <p className="mt-2 text-gray-600">Manage GPOs and their associated contracts</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowImport(true)}
                className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                title="Import GPOs from CSV"
              >
                <ImportIcon className="h-5 w-5 mr-2" />
                Import CSV
              </button>
              <button
                onClick={handleCreateNew}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                title="Add GPO"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add GPO
              </button>
            </div>
            {showImport && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <CSVImportModal
                  title="Import GPOs"
                  entityLabel="GPOs"
                  uploadFn={uploadGposCSV}
                  templateFn={downloadGpoTemplate}
                  note="GPOs are matched by code (or name) — existing are updated, new are created."
                  onUploadComplete={() => { setShowImport(false); queryClient.invalidateQueries({ queryKey: ['gpos'] }); }}
                  onClose={() => setShowImport(false)}
                />
              </div>
            )}
          </div>
        </div>

        {/* Statistics Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <Building2 className="h-8 w-8 text-blue-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total GPOs</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalGpos || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <TrendingUp className="h-8 w-8 text-green-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Active GPOs</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.activeGpos || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <FileText className="h-8 w-8 text-purple-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">With Contracts</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.gposWithContracts || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <Users className="h-8 w-8 text-orange-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Inactive GPOs</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.inactiveGpos || 0}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search GPOs by name, city, or state..."
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Filter Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                title="Toggle filters"
              >
                <Filter className="h-4 w-4 mr-2" />
                Filters
                {showFilters ? <ChevronUp className="h-4 w-4 ml-2" /> : <ChevronDown className="h-4 w-4 ml-2" />}
              </button>
            </div>

            {/* Filters */}
            {showFilters && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <input
                    type="text"
                    placeholder="Filter by state..."
                    value={stateFilter}
                    onChange={(e) => handleFilterChange('state', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                  <input
                    type="text"
                    placeholder="Filter by country..."
                    value={countryFilter}
                    onChange={(e) => handleFilterChange('country', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* GPOs Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading GPOs...</span>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('name')}
                      >
                        <div className="flex items-center">
                          Name
                          {getSortIcon('name')}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Location
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('createdAt')}
                      >
                        <div className="flex items-center">
                          Created
                          {getSortIcon('createdAt')}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contracts
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {gpos.map((gpo) => (
                      <tr key={gpo.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Building2 className="h-8 w-8 text-blue-600 mr-3" />
                            <div>
                              <div className="text-sm font-medium text-gray-900">{gpo.name}</div>
                              <div className="text-sm text-gray-500">ID: {gpo.id.slice(0, 8)}...</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-900">
                            <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                            <div>
                              {gpo.city && gpo.state ? (
                                <div>{gpo.city}, {gpo.state}</div>
                              ) : gpo.city ? (
                                <div>{gpo.city}</div>
                              ) : gpo.state ? (
                                <div>{gpo.state}</div>
                              ) : (
                                <div className="text-gray-400">No location</div>
                              )}
                              {gpo.country && (
                                <div className="text-xs text-gray-500">{gpo.country}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(gpo.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {gpo._count?.contracts || 0} contracts
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => handleView(gpo)}
                              className="text-blue-600 hover:text-blue-900 p-1"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleEdit(gpo)}
                              className="text-indigo-600 hover:text-indigo-900 p-1"
                              title="Edit GPO"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(gpo)}
                              className="text-red-600 hover:text-red-900 p-1"
                              title="Delete GPO"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="px-6 py-3 border-t border-gray-200 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} GPOs
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setPage(page - 1)}
                        disabled={!pagination.hasPrev}
                        className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Previous page"
                      >
                        Previous
                      </button>
                      <span className="px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded-md">
                        {pagination.page}
                      </span>
                      <button
                        onClick={() => setPage(page + 1)}
                        disabled={!pagination.hasNext}
                        className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Next page"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Empty State */}
              {gpos.length === 0 && !isLoading && (
                <div className="text-center py-12">
                  <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No GPOs Found</h3>
                  <p className="text-gray-500 mb-4">
                    {search || stateFilter || countryFilter 
                      ? 'No GPOs match your search criteria.' 
                      : 'Get started by creating your first GPO.'}
                  </p>
                  <button
                    onClick={handleCreateNew}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    title="Add GPO"
                  >
                    <Plus className="h-5 w-5 mr-2" />
                    Add GPO
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* GPO Modal */}
      {showModal && (
        <GPOModal
          gpo={editingGpo || viewingGpo}
          isOpen={showModal}
          onClose={handleCloseModal}
          isViewMode={!!viewingGpo}
        />
      )}
    </div>
  );
};

export default GPODashboard;
